import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"
import { slimSelectSettings } from "../helpers/slim_select_helpers"

// Minimum query length before the dropdown queries the server.
const MIN_SERVER_SEARCH_LENGTH = 2
// Collapse a burst of keystrokes into a single request — the search endpoint embeds the
// query via a rate-limited external API, so one request per keystroke must be avoided.
const SERVER_SEARCH_DEBOUNCE_MS = 250

// Manages the boq_position custom-field allocation sub-table on the configurable
// timesheet form. Rows (LV position + hours) are serialized into a hidden input as
// JSON { net, rows: [{ boq_item_id, hours }] }, where net is the entry's worked time.
// Positions load from the form's selected site and reload when it changes; each row's
// select is a SlimSelect whose typing triggers the hybrid keyword+semantic search on
// the server (so synonyms surface), falling back to the full preloaded list when the
// search box is short/empty. The server books each row's hours, scaling by the record's
// share of `net` so overnight-split entries (which share this data) sum back to the
// entered hours.
export default class extends Controller {
  static targets = [
    "value",
    "rows",
    "rowTemplate",
    "total",
    "net",
    "warning",
    "message",
    "addButton",
  ]

  static values = {
    baseUrl: String,
    selectPlaceholder: String,
    searching: String,
    searchError: String,
    noSite: String,
    noPositions: String,
  }

  connect() {
    this.positions = []
    this.form = this.element.closest("form")
    this.siteSelect = this.form?.querySelector('select[name$="[site_id]"]')
    this.timeInputs = ["time_from", "time_to", "time_break"].map((name) =>
      this.form?.querySelector(`[name$="[${name}]"]`),
    )
    this.onSiteChange = this.onSiteChange.bind(this)
    this.onTimeChange = this.onTimeChange.bind(this)
    if (this.siteSelect) this.siteSelect.addEventListener("change", this.onSiteChange)
    this.timeInputs.forEach((input) => {
      if (input) {
        input.addEventListener("change", this.onTimeChange)
        input.addEventListener("input", this.onTimeChange)
      }
    })
    this.loadPositions().then(() => this.renderFromValue())
  }

  disconnect() {
    this.cancelServerSearch()
    if (this.siteSelect) this.siteSelect.removeEventListener("change", this.onSiteChange)
    this.timeInputs.forEach((input) => {
      if (input) {
        input.removeEventListener("change", this.onTimeChange)
        input.removeEventListener("input", this.onTimeChange)
      }
    })
    this.rowsTarget
      .querySelectorAll('[data-role="position"]')
      .forEach((select) => this.destroySelect(select))
  }

  get siteId() {
    return this.siteSelect ? this.siteSelect.value : ""
  }

  // Entry worked time in hours, computed from the form time fields. Handles overnight
  // entries (end <= start wraps to the next day) and subtracts the break.
  get netHours() {
    const [from, to, brk] = this.timeInputs.map((input) => this.parseMinutes(input?.value))
    if (from === null || to === null) return 0
    let span = to - from
    if (span <= 0) span += 24 * 60
    const net = span - (brk || 0)
    return net > 0 ? Math.round((net / 60) * 100) / 100 : 0
  }

  parseMinutes(value) {
    if (!value) return null
    const [h, m] = value.split(":").map((n) => parseInt(n, 10))
    if (Number.isNaN(h) || Number.isNaN(m)) return null
    return h * 60 + m
  }

  async loadPositions() {
    if (!this.siteId) {
      this.positions = []
      return
    }
    try {
      const response = await fetch(`${this.baseUrlValue}/${this.siteId}/boq_positions`)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.positions = await response.json()
    } catch (error) {
      console.error("Failed to load BoQ positions:", error)
      this.positions = []
    }
  }

  onSiteChange() {
    this.loadPositions().then(() => {
      this.refreshRowOptions()
      this.serialize()
    })
  }

  onTimeChange() {
    this.serialize()
  }

  renderFromValue() {
    this.rowsTarget.innerHTML = ""
    this.parseValue().forEach((row) => this.appendRow(row.boq_item_id, row.hours))
    this.updateState()
  }

  appendRow(boqItemId, hours) {
    const fragment = this.rowTemplateTarget.content.cloneNode(true)
    const row = fragment.querySelector("[data-row]")
    const select = row.querySelector('[data-role="position"]')
    this.fillSelect(select, boqItemId)
    if (hours !== undefined && hours !== null && hours !== "") {
      row.querySelector('[data-role="hours"]').value = hours
    }
    this.rowsTarget.appendChild(row)
    this.enhanceSelect(select)
  }

  fillSelect(select, selectedId) {
    select.innerHTML = ""
    const blank = document.createElement("option")
    blank.value = ""
    blank.text = this.selectPlaceholderValue
    select.appendChild(blank)
    this.positions.forEach((position) => {
      const option = document.createElement("option")
      option.value = position.id
      option.text = position.label
      if (String(position.id) === String(selectedId)) option.selected = true
      select.appendChild(option)
    })
  }

  // Wrap a row's native select with SlimSelect; typing ≥2 chars queries the hybrid
  // search endpoint, while a short/empty box restores the full preloaded list.
  enhanceSelect(select) {
    if (select._slim) return
    const placeholder = this.selectPlaceholderValue
    const fullData = [{ text: placeholder, value: "" }].concat(
      this.positions.map((position) => ({ text: position.label, value: String(position.id) })),
    )
    select._slim = new SlimSelect({
      select,
      settings: slimSelectSettings({
        searchPlaceholder: "Suchen",
        searchingText: this.hasSearchingValue ? this.searchingValue : "Suchen …",
        placeholderText: placeholder,
        allowDeselect: true,
        closeOnSelect: true,
      }),
      events: {
        search: (search) => this.serverSearch(search, fullData),
      },
    })
  }

  // Debounced and with the previous in-flight request aborted, so a burst of keystrokes
  // makes one query, not one per character (the endpoint embeds the query via a
  // rate-limited external API). A short/empty box restores the full preloaded list.
  serverSearch(search, fullData) {
    const term = search.trim()
    this.cancelServerSearch(fullData)
    if (term.length < MIN_SERVER_SEARCH_LENGTH || !this.siteId) return fullData

    return new Promise((resolve, reject) => {
      // Settle any superseded request with the full list so its promise never dangles.
      this.settlePendingSearch = () => resolve(fullData)
      this.searchAbort = new AbortController()
      this.searchTimer = setTimeout(() => {
        fetch(`${this.baseUrlValue}/${this.siteId}/boq_positions?q=${encodeURIComponent(term)}`, {
          headers: { Accept: "application/json" },
          signal: this.searchAbort.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            return response.json()
          })
          .then((rows) => resolve(rows.map((row) => ({ text: row.label, value: String(row.id) }))))
          .catch((error) => {
            if (error.name === "AbortError") resolve(fullData)
            else
              reject(
                new Error(
                  this.hasSearchErrorValue ? this.searchErrorValue : "Suche fehlgeschlagen",
                ),
              )
          })
      }, SERVER_SEARCH_DEBOUNCE_MS)
    })
  }

  // Cancel a pending debounce timer + in-flight request, settling its promise with fallback.
  cancelServerSearch(fallback) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
    }
    if (this.searchAbort) {
      this.searchAbort.abort()
      this.searchAbort = null
    }
    if (this.settlePendingSearch) {
      this.settlePendingSearch(fallback)
      this.settlePendingSearch = null
    }
  }

  destroySelect(select) {
    if (select._slim) {
      select._slim.destroy()
      delete select._slim
    }
  }

  refreshRowOptions() {
    this.rowsTarget.querySelectorAll('[data-role="position"]').forEach((select) => {
      const current = select.value
      this.destroySelect(select)
      this.fillSelect(select, current)
      this.enhanceSelect(select)
    })
  }

  addRow(event) {
    event.preventDefault()
    this.appendRow("", "")
    this.updateState()
  }

  removeRow(event) {
    event.preventDefault()
    const row = event.target.closest("[data-row]")
    this.destroySelect(row.querySelector('[data-role="position"]'))
    row.remove()
    this.serialize()
  }

  // Write { net, rows } into the hidden input (empty string when no rows, so a required
  // field reads as blank), then refresh the total/warning/message.
  serialize() {
    const rows = []
    this.rowsTarget.querySelectorAll("[data-row]").forEach((row) => {
      const boqItemId = row.querySelector('[data-role="position"]').value
      const hours = row.querySelector('[data-role="hours"]').value
      if (boqItemId && hours) rows.push({ boq_item_id: boqItemId, hours: hours })
    })
    this.valueTarget.value = rows.length ? JSON.stringify({ net: this.netHours, rows: rows }) : ""
    this.updateState()
  }

  updateState() {
    const net = this.netHours
    let total = 0
    this.rowsTarget.querySelectorAll('[data-role="hours"]').forEach((input) => {
      total += parseFloat(input.value) || 0
    })
    this.totalTarget.textContent = this.formatHours(total)
    this.netTarget.textContent = this.formatHours(net)
    this.warningTarget.classList.toggle("hidden", !(net > 0 && total > net + 0.001))

    const hasSite = Boolean(this.siteId)
    const hasPositions = this.positions.length > 0
    if (!hasSite) {
      this.messageTarget.textContent = this.noSiteValue
    } else if (!hasPositions) {
      this.messageTarget.textContent = this.noPositionsValue
    } else {
      this.messageTarget.textContent = ""
    }
    const disabled = !hasSite || !hasPositions
    this.addButtonTarget.disabled = disabled
    this.addButtonTarget.classList.toggle("opacity-50", disabled)
    this.addButtonTarget.classList.toggle("cursor-not-allowed", disabled)
  }

  formatHours(value) {
    return (Math.round(value * 100) / 100).toString()
  }

  parseValue() {
    const raw = this.valueTarget.value
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed.rows) ? parsed.rows : []
    } catch {
      return []
    }
  }
}
